import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  ShieldCheckIcon,
  DocumentTextIcon,
  LockClosedIcon,
  ServerIcon,
  ArrowPathIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
  HandRaisedIcon,
  GlobeAmericasIcon,
  PencilSquareIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  BookOpenIcon,
  CreditCardIcon,
  ExclamationCircleIcon,
  CloudArrowUpIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { ScrollAnimation } from '../components/ScrollAnimation';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const EULAWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f3f4f6 0%, #ffffff 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.03) 0%, transparent 70%);
    animation: ${gradientShift} 20s ease infinite;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  padding: var(--spacing-4xl) 0;
  position: relative;
  overflow: hidden;
  margin-top: -80px;
  padding-top: calc(var(--spacing-4xl) + 80px);
  
  @media (min-width: 768px) {
    margin-top: -90px;
    padding-top: calc(var(--spacing-4xl) + 90px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-xl);
  position: relative;
  z-index: 1;
  text-align: center;
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  margin-bottom: var(--spacing-xl);
  animation: ${fadeIn} 0.6s ease-out;
  
  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: var(--spacing-lg);
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
  max-width: 600px;
  margin: 0 auto var(--spacing-xl) auto;
  line-height: 1.8;
  animation: ${fadeIn} 1s ease-out;
`;

const LastUpdatedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  background: rgba(255, 255, 255, 0.15);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: 50px;
  color: white;
  font-size: 0.9rem;
  animation: ${fadeIn} 1.2s ease-out;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--spacing-3xl) var(--spacing-xl);
  position: relative;
  z-index: 1;
`;

const QuickNav = styled.div`
  background: white;
  border-radius: 16px;
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-3xl);
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  animation: ${fadeIn} 0.6s ease-out;
`;

const QuickNavTitle = styled.h3`
  color: var(--davinci-midnight);
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--davinci-blue);
  }
`;

const QuickNavGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-md);
`;

const QuickNavItem = styled.a`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  color: var(--text-secondary);
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(30, 58, 138, 0.05) 100%);
    color: var(--davinci-blue);
    transform: translateX(4px);
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: var(--davinci-blue);
    flex-shrink: 0;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  gap: var(--spacing-2xl);
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: var(--spacing-2xl);
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 30px rgba(0,0,0,0.1);
    transform: translateY(-2px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(30, 58, 138, 0.1) 100%);
  border-radius: 12px;
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--davinci-blue);
  }
`;

const SectionContent = styled.div`
  flex: 1;
`;

const SectionTitle = styled.h2`
  color: var(--davinci-midnight);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

const SectionNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  color: white;
  border-radius: 50%;
  font-size: 0.9rem;
  font-weight: 600;
`;

const SectionText = styled.p`
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: var(--spacing-lg);
`;

const ListItem = styled.li`
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: var(--spacing-md);
  padding-left: var(--spacing-xl);
  position: relative;
  
  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 10px;
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  &:after {
    content: "✓";
    position: absolute;
    left: 5px;
    top: 8px;
    color: white;
    font-size: 12px;
    font-weight: bold;
  }
`;

const UnorderedList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 var(--spacing-lg) 0;
`;

const ImportantNote = styled.div`
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(185, 28, 28, 0.05) 100%);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 12px;
  padding: var(--spacing-lg);
  margin: var(--spacing-2xl) 0;
  display: flex;
  gap: var(--spacing-lg);
  align-items: flex-start;
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--color-error);
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const ImportantText = styled.p`
  color: var(--davinci-midnight);
  font-weight: 500;
  line-height: 1.8;
  margin: 0;
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, var(--davinci-midnight) 0%, var(--davinci-blue) 100%);
  border-radius: 16px;
  padding: var(--spacing-2xl);
  margin-top: var(--spacing-3xl);
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  }
`;

const ContactContent = styled.div`
  position: relative;
  z-index: 1;
`;

const ContactTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-xl);
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  
  svg {
    width: 28px;
    height: 28px;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-xl);
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  
  svg {
    width: 20px;
    height: 20px;
    opacity: 0.8;
  }
  
  a {
    color: white;
    text-decoration: none;
    transition: opacity 0.3s ease;
    
    &:hover {
      opacity: 0.8;
    }
  }
`;

const EULA: React.FC = () => {
  const sections = [
    { id: 'definitions', title: 'Definitions', icon: <BookOpenIcon /> },
    { id: 'license-grant', title: 'License Grant', icon: <CheckCircleIcon /> },
    { id: 'restrictions', title: 'License Restrictions', icon: <XCircleIcon /> },
    { id: 'ownership', title: 'Ownership & IP', icon: <ShieldCheckIcon /> },
    { id: 'privacy', title: 'Data Privacy', icon: <LockClosedIcon /> },
    { id: 'updates', title: 'Updates', icon: <ArrowPathIcon /> },
    { id: 'termination', title: 'Termination', icon: <HandRaisedIcon /> },
    { id: 'warranties', title: 'Warranties', icon: <ExclamationTriangleIcon /> },
    { id: 'liability', title: 'Liability', icon: <ScaleIcon /> },
    { id: 'indemnification', title: 'Indemnification', icon: <DocumentTextIcon /> },
    { id: 'governing', title: 'Governing Law', icon: <GlobeAmericasIcon /> },
    { id: 'amendments', title: 'Amendments', icon: <PencilSquareIcon /> },
    { id: 'acceptable-use', title: 'Acceptable Use', icon: <ExclamationCircleIcon /> },
    { id: 'data-backup', title: 'Data Backup', icon: <CloudArrowUpIcon /> },
    { id: 'survival', title: 'Survival', icon: <ClockIcon /> },
    { id: 'contact', title: 'Contact', icon: <ChatBubbleLeftRightIcon /> },
  ];

  return (
    <EULAWrapper>
      <HeroSection>
        <HeroContent>
          <IconWrapper>
            <ShieldCheckIcon />
          </IconWrapper>
          <HeroTitle>End User License Agreement</HeroTitle>
          <HeroSubtitle>
            Your agreement with Davinci AI Solutions for the use of our software, services, and tools
          </HeroSubtitle>
          <LastUpdatedBadge>
            <InformationCircleIcon />
            Last Updated: August 29, 2025
          </LastUpdatedBadge>
        </HeroContent>
      </HeroSection>

      <Container>
        <ScrollAnimation>
          <QuickNav>
            <QuickNavTitle>
              <DocumentTextIcon />
              Quick Navigation
            </QuickNavTitle>
            <QuickNavGrid>
              {sections.map((section) => (
                <QuickNavItem key={section.id} href={`#${section.id}`}>
                  {section.icon}
                  {section.title}
                </QuickNavItem>
              ))}
            </QuickNavGrid>
          </QuickNav>
        </ScrollAnimation>

        <ScrollAnimation>
          <ImportantNote>
            <ExclamationTriangleIcon />
            <ImportantText>
              This End User License Agreement ("Agreement") is a legal agreement between you and HL Networks Inc. OA Davinci AI Solutions. 
              By using our Services, you agree to be bound by these terms. If you do not agree, you must discontinue use of the Services.
            </ImportantText>
          </ImportantNote>
        </ScrollAnimation>

        <ContentGrid>
          <ScrollAnimation>
            <SectionCard id="definitions">
              <SectionHeader>
                <SectionIcon>
                  <BookOpenIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>1</SectionNumber>
                    Definitions
                  </SectionTitle>
                  <SectionText>
                    For the purposes of this Agreement, the following terms shall have the meanings set forth below:
                  </SectionText>
                  <UnorderedList>
                    <ListItem>
                      <strong>"Services"</strong> means all software, applications, tools, platforms, and related services 
                      provided by Davinci AI Solutions, including but not limited to managed IT services, helpdesk support, 
                      software development, and AI solutions.
                    </ListItem>
                    <ListItem>
                      <strong>"User," "you," or "your"</strong> refers to the individual or entity accessing or using the Services.
                    </ListItem>
                    <ListItem>
                      <strong>"Software"</strong> means any applications, programs, or code provided as part of the Services.
                    </ListItem>
                    <ListItem>
                      <strong>"Client Data"</strong> means any data, information, or content that you provide to us or that 
                      we collect on your behalf in connection with your use of the Services.
                    </ListItem>
                    <ListItem>
                      <strong>"Agreement"</strong> refers to this End User License Agreement and any amendments or modifications thereto.
                    </ListItem>
                  </UnorderedList>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="license-grant">
              <SectionHeader>
                <SectionIcon>
                  <CheckCircleIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>2</SectionNumber>
                    License Grant
                  </SectionTitle>
                  <SectionText>
                    HL Networks Inc. OA Davinci AI Solutions grants you a non-exclusive, non-transferable, revocable 
                    license to use our Services solely for your personal or internal business purposes, in accordance with 
                    the terms of this Agreement. This license does not grant you ownership of the Services or any related 
                    intellectual property.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="restrictions">
              <SectionHeader>
                <SectionIcon>
                  <XCircleIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>3</SectionNumber>
                    License Restrictions
                  </SectionTitle>
                  <SectionText>You agree not to:</SectionText>
                  <UnorderedList>
                    <ListItem>
                      Distribute, lease, sublicense, or otherwise transfer the Services to any third party
                    </ListItem>
                    <ListItem>
                      Modify, adapt, reverse-engineer, decompile, or disassemble any part of the Services, except where 
                      such activities are expressly permitted by law
                    </ListItem>
                    <ListItem>
                      Use the Services for any illegal or unauthorized purposes, or in a way that violates any applicable 
                      local, state, national, or international law or regulation
                    </ListItem>
                    <ListItem>
                      Circumvent any technological measures designed to control access to the Services or related systems
                    </ListItem>
                  </UnorderedList>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="ownership">
              <SectionHeader>
                <SectionIcon>
                  <ShieldCheckIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>4</SectionNumber>
                    Ownership and Intellectual Property
                  </SectionTitle>
                  <SectionText>
                    All intellectual property rights, including but not limited to copyrights, patents, trademarks, and 
                    trade secrets, related to the Services are owned by HL Networks Inc. OA Davinci AI Solutions 
                    or our licensors. You acknowledge that no title or ownership of the Services is transferred to you 
                    and that you do not acquire any rights other than those specifically granted in this Agreement.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="privacy">
              <SectionHeader>
                <SectionIcon>
                  <LockClosedIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>5</SectionNumber>
                    Data Privacy and Security
                  </SectionTitle>
                  <SectionText>
                    Your use of the Services is also governed by our Privacy Policy (https://www.davincisolutions.ai/privacy), 
                    which outlines how we collect, use, and protect your personal information. By using the Services, you 
                    consent to the collection and use of your information in accordance with our Privacy Policy.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="updates">
              <SectionHeader>
                <SectionIcon>
                  <ArrowPathIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>6</SectionNumber>
                    Updates and Modifications
                  </SectionTitle>
                  <SectionText>
                    We may provide updates or modifications to the Services to improve performance, fix bugs, or add new 
                    features. You agree that such updates or modifications will be subject to this Agreement unless 
                    otherwise specified. You may be required to install updates to continue using the Services.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="termination">
              <SectionHeader>
                <SectionIcon>
                  <HandRaisedIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>7</SectionNumber>
                    Termination
                  </SectionTitle>
                  <SectionText>
                    This Agreement is effective until terminated. We may terminate your access to the Services at any time, 
                    with or without notice, for any violation of the terms of this Agreement. Upon termination, your rights 
                    to use the Services will immediately cease, and you must uninstall or stop using any software or tools 
                    associated with the Services.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="warranties">
              <SectionHeader>
                <SectionIcon>
                  <ExclamationTriangleIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>8</SectionNumber>
                    Disclaimer of Warranties
                  </SectionTitle>
                  <SectionText>
                    The Services are provided on an "as-is" and "as available" basis, without any warranties of any kind, 
                    express or implied, including but not limited to warranties of merchantability, fitness for a particular 
                    purpose, or non-infringement. We do not warrant that the Services will be uninterrupted or error-free, 
                    or that any defects will be corrected.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="liability">
              <SectionHeader>
                <SectionIcon>
                  <ScaleIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>9</SectionNumber>
                    Limitation of Liability
                  </SectionTitle>
                  <SectionText>
                    In no event shall HL Networks Inc. OA Davinci AI Solutions be liable for any indirect, incidental, 
                    special, consequential, or punitive damages arising out of or related to your use or inability to use the 
                    Services, including but not limited to loss of profits, data, or business, even if we have been advised 
                    of the possibility of such damages.
                  </SectionText>
                  <SectionText>
                    Our total liability to you for any claim arising out of or related to this Agreement or your use of the 
                    Services will not exceed the amount you paid for the Services.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="indemnification">
              <SectionHeader>
                <SectionIcon>
                  <DocumentTextIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>10</SectionNumber>
                    Indemnification
                  </SectionTitle>
                  <SectionText>
                    You agree to indemnify and hold harmless HL Networks Inc. OA Davinci AI Solutions, its affiliates, 
                    employees, officers, and agents from any claims, damages, losses, liabilities, costs, or expenses arising 
                    from your use of the Services, your violation of this Agreement, or your violation of any applicable laws 
                    or third-party rights.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="governing">
              <SectionHeader>
                <SectionIcon>
                  <GlobeAmericasIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>11</SectionNumber>
                    Governing Law
                  </SectionTitle>
                  <SectionText>
                    This Agreement will be governed by and construed in accordance with the laws of the Province of Alberta, 
                    Canada, without regard to its conflict of law provisions. Any legal action or proceeding arising under 
                    this Agreement will be brought exclusively in the courts of the Province of Alberta, and you 
                    hereby consent to the jurisdiction of those courts.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="amendments">
              <SectionHeader>
                <SectionIcon>
                  <PencilSquareIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>12</SectionNumber>
                    Amendments to the Agreement
                  </SectionTitle>
                  <SectionText>
                    We reserve the right to modify this Agreement at any time. We will notify you of any changes by posting 
                    the updated Agreement on our website. Your continued use of the Services following any amendments 
                    constitutes your acceptance of the revised terms.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="acceptable-use">
              <SectionHeader>
                <SectionIcon>
                  <ExclamationCircleIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>13</SectionNumber>
                    Acceptable Use Policy
                  </SectionTitle>
                  <SectionText>
                    You agree to use the Services only for lawful purposes and in accordance with this Agreement. 
                    You are prohibited from:
                  </SectionText>
                  <UnorderedList>
                    <ListItem>Using the Services in any way that violates any applicable federal, provincial, local, or international law or regulation</ListItem>
                    <ListItem>Transmitting any material that is defamatory, offensive, or otherwise objectionable</ListItem>
                    <ListItem>Impersonating any person or entity or misrepresenting your affiliation with any person or entity</ListItem>
                    <ListItem>Interfering with or disrupting the Services or servers or networks connected to the Services</ListItem>
                    <ListItem>Attempting to gain unauthorized access to any portion of the Services or any other systems or networks</ListItem>
                    <ListItem>Using the Services to transmit any viruses, worms, defects, Trojan horses, or other items of a destructive nature</ListItem>
                  </UnorderedList>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="data-backup">
              <SectionHeader>
                <SectionIcon>
                  <CloudArrowUpIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>14</SectionNumber>
                    Data Backup Responsibility
                  </SectionTitle>
                  <SectionText>
                    While we implement robust backup and disaster recovery procedures as part of our Services, you acknowledge 
                    and agree that you are responsible for maintaining your own backups of your Client Data. We recommend that 
                    you regularly export and backup any critical data. We shall not be liable for any loss of data that could 
                    have been avoided by maintaining adequate backups.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="survival">
              <SectionHeader>
                <SectionIcon>
                  <ClockIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>15</SectionNumber>
                    Survival
                  </SectionTitle>
                  <SectionText>
                    The following sections shall survive termination of this Agreement: Definitions (Section 1), 
                    Ownership and Intellectual Property (Section 4), Disclaimer of Warranties (Section 8), 
                    Limitation of Liability (Section 9), Indemnification (Section 10), Governing Law (Section 11), 
                    and this Survival provision (Section 15), along with any other provisions that by their nature 
                    should survive termination.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>
        </ContentGrid>

        <ScrollAnimation>
          <ContactCard id="contact">
            <ContactContent>
              <ContactTitle>
                <ChatBubbleLeftRightIcon />
                Questions? Contact Us
              </ContactTitle>
              <ContactGrid>
                <ContactItem>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href="mailto:info@davincisolutions.ai">info@davincisolutions.ai</a>
                </ContactItem>
                <ContactItem>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href="tel:+14038799343">403-879-9343</a>
                </ContactItem>
                <ContactItem>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>11-6320 11 Street SE, Calgary, AB T2H 2L7</span>
                </ContactItem>
              </ContactGrid>
            </ContactContent>
          </ContactCard>
        </ScrollAnimation>
      </Container>
    </EULAWrapper>
  );
};

export default EULA;